import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ChevronRight } from 'lucide-react';
import HeroImageOnly from '../components/layout/HeroImageOnly';
import BreadcrumbMarker from '../components/layout/BreadcrumbMarker';

const VirtualTwilight = () => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Hero Section - Clean Image Only */}
      <HeroImageOnly 
        imageSrc="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/7ocny9bd_Hero-image-twilight.jpg"
        imageAlt="Luxury home at twilight"
      />

      {/* Breadcrumb Section */}
      <BreadcrumbMarker 
        parentLabel="Services"
        parentHref="/services"
        currentLabel="Virtual Twilight"
      />

      {/* Virtual Twilight Photos Section */}
      <section className="py-16 md:py-20 bg-[#FAF9F6]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            
            {/* Left Column - Copy + Features */}
            <div className="order-2 lg:order-1">
              <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">
                Virtual Twilight
              </p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#050816] mb-6 leading-tight">
                Turn Daylight into a Twilight Hero Image
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                We take your real daytime exterior photo and transform it into a stunning twilight version—complete with a cinematic sky, warm interior glow, and professional color grading. The result looks natural, premium, and ready to stop the scroll.
              </p>
              <p className="text-gray-500 leading-relaxed mb-8">
                No need to schedule around sunset. No weather delays. Just send your daytime shot, and we'll deliver a polished twilight image that makes your listing unforgettable.
              </p>

              {/* Feature Chips */}
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  Realistic Window Glow
                </span>
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  Cinematic Sky & Color
                </span>
                <span className="px-4 py-2 bg-[#050816] text-white text-sm font-medium rounded-full">
                  MLS-Friendly Delivery
                </span>
              </div>

              {/* Perfect For Line */}
              <p className="text-sm text-gray-500 italic mb-8">
                Perfect for luxury listings, pools, views, and first-impression marketing.
              </p>

              {/* CTA Buttons */}
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
                >
                  <Link to="/book?type=other">
                    Order Now
                    <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
                <Button 
                  asChild 
                  className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all"
                >
                  <Link to="/pricing">
                    View Pricing
                  </Link>
                </Button>
              </div>
            </div>

            {/* Right Column - Before/After Image Card */}
            <div className="order-1 lg:order-2">
              <div className="relative group">
                {/* Image Card */}
                <div className="overflow-hidden rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/1zqt82zq_ChatGPT%20Image%20Feb%2010%2C%202026%2C%2006_03_23%20PM.png"
                    alt="Day to Virtual Twilight transformation"
                    className="w-full h-auto object-cover"
                  />
                </div>
                
                {/* Before/After Labels */}
                <div className="absolute bottom-0 left-0 right-0 flex justify-between px-4 pb-4 pointer-events-none">
                  <span className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-[#050816] text-xs font-semibold uppercase tracking-wider rounded-full shadow-md">
                    Before
                  </span>
                  <span className="px-3 py-1.5 bg-[#050816]/90 backdrop-blur-sm text-[#F5C056] text-xs font-semibold uppercase tracking-wider rounded-full shadow-md">
                    After
                  </span>
                </div>

                {/* Caption Overlay */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2">
                  <span className="px-4 py-2 bg-[#050816] text-white text-xs font-medium uppercase tracking-widest rounded-full shadow-lg whitespace-nowrap">
                    Day → Virtual Twilight
                  </span>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default VirtualTwilight;
